// services/localQueryService.react.js
// React-compatible version of localQueryService

// Import the JSON data directly for React environment
import standardQueriesData from '../data/standardQueries.compact.json';

/**
 * Local Query Service - Fast local search for standard queries
 * No Firebase calls needed, works offline
 * React-compatible version
 */
class LocalQueryService {
  constructor() {
    this.queries = standardQueriesData.queries || [];
    this.metadata = standardQueriesData.metadata || {};
    this.searchCache = new Map(); // Cache search results
  }

  /**
   * Search queries by text with filters
   * @param {Object} options - Search options
   * @param {string} options.searchTerm - Text to search for
   * @param {string} options.queryType - Filter by query type
   * @param {string} options.department - Filter by department
   * @param {string} options.requirement - Filter by requirement
   * @param {number} options.limit - Limit number of results (default: 10)
   * @returns {Array} Array of matching queries
   */
  searchQueries({
    searchTerm = '',
    queryType = '',
    department = '',
    requirement = '',
    limit = 10
  } = {}) {
    // Create cache key
    const cacheKey = `${searchTerm}_${queryType}_${department}_${requirement}_${limit}`;
    
    // Check cache first
    if (this.searchCache.has(cacheKey)) {
      return this.searchCache.get(cacheKey);
    }

    let results = [...this.queries];

    // Filter by query type
    if (queryType && queryType !== 'all') {
      results = results.filter(query => 
        query.queryType && query.queryType.toLowerCase() === queryType.toLowerCase()
      );
    }

    // Filter by department
    if (department && department !== 'all') {
      results = results.filter(query => 
        query.department && query.department.toLowerCase() === department.toLowerCase()
      );
    }

    // Filter by requirement
    if (requirement && requirement !== 'all') {
      results = results.filter(query => 
        query.requirementStages && 
        query.requirementStages.some(stage => 
          stage.toLowerCase().includes(requirement.toLowerCase())
        )
      );
    }

    // Text search
    if (searchTerm && searchTerm.trim().length >= 2) {
      const searchLower = searchTerm.toLowerCase().trim();
      results = results.filter(query => {
        return query.searchText && query.searchText.includes(searchLower);
      });

      // Sort by relevance (exact matches first, then partial matches)
      results.sort((a, b) => {
        const aHeader = a.queryHeader?.toLowerCase() || '';
        const bHeader = b.queryHeader?.toLowerCase() || '';
        const aDesc = a.queryDescription?.toLowerCase() || '';
        const bDesc = b.queryDescription?.toLowerCase() || '';

        // Exact header match gets highest priority
        if (aHeader.includes(searchLower) && !bHeader.includes(searchLower)) return -1;
        if (!aHeader.includes(searchLower) && bHeader.includes(searchLower)) return 1;

        // Then description match
        if (aDesc.includes(searchLower) && !bDesc.includes(searchLower)) return -1;
        if (!aDesc.includes(searchLower) && bDesc.includes(searchLower)) return 1;

        // Finally by header length (shorter titles first)
        return aHeader.length - bHeader.length;
      });
    }

    // Apply limit
    const limitedResults = results.slice(0, limit);

    // Cache results
    this.searchCache.set(cacheKey, limitedResults);

    return limitedResults;
  }

  /**
   * Get query by ID
   * @param {string} queryId - Query ID
   * @returns {Object|null} Query object or null if not found
   */
  getQueryById(queryId) {
    return this.queries.find(query => query.id === queryId) || null;
  }

  /**
   * Get all available query types
   * @returns {Array} Array of unique query types
   */
  getQueryTypes() {
    const types = [...new Set(this.queries.map(q => q.queryType).filter(Boolean))];
    return types.sort();
  }

  /**
   * Get all available departments
   * @returns {Array} Array of unique departments
   */
  getDepartments() {
    const departments = [...new Set(this.queries.map(q => q.department).filter(Boolean))];
    return departments.sort();
  }

  /**
   * Get all available requirements
   * @returns {Array} Array of unique requirements
   */
  getRequirements() {
    const requirements = [...new Set(this.queries.map(q => q.requirement).filter(Boolean))];
    return requirements.sort();
  }

  /**
   * Get queries by specific filters
   * @param {Object} filters - Filter object
   * @returns {Array} Filtered queries
   */
  getQueriesByFilter(filters = {}) {
    return this.searchQueries(filters);
  }

  /**
   * Get popular/frequently used queries
   * @param {number} limit - Number of queries to return
   * @returns {Array} Array of popular queries
   */
  getPopularQueries(limit = 5) {
    // For now, return the first few queries of different types
    const popularTypes = ['Document', 'Investigation', 'Discharge'];
    const popular = [];

    popularTypes.forEach(type => {
      const typeQueries = this.queries.filter(q => 
        q.queryType && q.queryType.includes(type)
      );
      if (typeQueries.length > 0) {
        popular.push(typeQueries[0]);
      }
    });

    return popular.slice(0, limit);
  }

  /**
   * Clear search cache (call when needed to free memory)
   */
  clearCache() {
    this.searchCache.clear();
  }

  /**
   * Get service metadata
   * @returns {Object} Metadata about the queries dataset
   */
  getMetadata() {
    return {
      ...this.metadata,
      totalQueriesLoaded: this.queries.length,
      cacheSize: this.searchCache.size,
      isLocal: true
    };
  }
}

// Create singleton instance
const localQueryService = new LocalQueryService();

// Export service methods for use in React components
export const getQueriesByFilter = (filters) => {
  return {
    success: true,
    queries: localQueryService.getQueriesByFilter(filters),
    totalCount: localQueryService.getQueriesByFilter(filters).length
  };
};

export const searchStandardQueries = (searchTerm, options = {}) => {
  return {
    success: true,
    queries: localQueryService.searchQueries({ 
      searchTerm, 
      ...options 
    })
  };
};

export const getQueryById = (queryId) => {
  const query = localQueryService.getQueryById(queryId);
  return {
    success: !!query,
    query: query
  };
};

export const getQueryTypes = () => {
  return {
    success: true,
    types: localQueryService.getQueryTypes()
  };
};

export const getDepartments = () => {
  return {
    success: true,
    departments: localQueryService.getDepartments()
  };
};

export const getRequirements = () => {
  return {
    success: true,
    requirements: localQueryService.getRequirements()
  };
};

export const getPopularQueries = (limit = 5) => {
  return {
    success: true,
    queries: localQueryService.getPopularQueries(limit)
  };
};

export const getServiceMetadata = () => {
  return {
    success: true,
    metadata: localQueryService.getMetadata()
  };
};

export default localQueryService;
